/**
 * @file Sandbox mode configuration and state.
 *
 * Controls which enemy types are active, terrain mode, spray mode state,
 * and other sandbox knobs. All values are mutable — the UI and the game
 * systems read them live.
 */

import { ENEMIES } from '../config/Enemies.js';
import { loadDebugPrefs, hydrate } from '../config/DebugPrefs.js';

/** @type {Readonly<string[]>} */
export const ENEMY_KEYS = Object.freeze(Object.keys(ENEMIES));

/**
 * Terrain generation modes.
 * @readonly
 * @enum {string}
 */
export const TerrainMode = Object.freeze({
  /** Completely flat plane — no walls, no obstacles. */
  FLAT: 'flat',
  /** Flat plane with randomly placed obstacles. */
  FLAT_OBSTACLES: 'flat_obstacles',
  /** Full procedural dungeon with rooms and corridors. */
  FULL: 'full'
});

/**
 * Current sandbox state. Mutated by the UI and read by systems.
 */
export const sandboxState = {
  /** Whether the sandbox panel is visible. */
  enabled: false,

  /** Whether spray mode is active (click to place enemies). */
  sprayMode: false,

  /** Which enemy type to spawn when spraying. */
  sprayType: 'grunt',

  /** How many enemies to spawn per click in spray mode. */
  sprayCount: 5,

  /** How many enemies to spawn per click in single-place mode. */
  singleCount: 1,

  /** Active terrain mode. */
  terrainMode: TerrainMode.FULL,

  /** Per-enemy-type toggles (true = enabled). */
  enemyToggles: {},

  /**
   * Developer cheats, all off by default. Gameplay systems read these
   * directly (same pattern as the wave director reading `enemyToggles`).
   */
  cheats: {
    /** Player takes no damage and the kill timer stops draining. */
    godMode: false,
    /** The player's magazine tops itself up every frame. */
    infiniteAmmo: false,
    /** Anything the player damages dies in one hit. */
    oneShotKills: false,
    /** Player takes no damage, but the kill timer still drains. */
    immortal: false,
    /** The kill timer stops draining, but the player can still be hurt. */
    pulseFrozen: false
  },

  /** Whether to delete enemies on right-click. */
  deleteMode: false,

  /** Number of obstacles for flat_obstacles mode. */
  obstacleCount: 12
};

// Survive vite reloads: yesterday's toggles are today's toggles until reset.
hydrate(loadDebugPrefs()?.sandbox, sandboxState,
  ['enabled', 'terrainMode', 'sprayType'], ['enemyToggles', 'cheats']);


// Initialise toggles from the ENEMIES config.
for (const key of ENEMY_KEYS) {
  sandboxState.enemyToggles[key] = true;
}

/**
 * Get a list of enabled enemy keys.
 * @returns {string[]}
 */
export function getEnabledEnemies() {
  return ENEMY_KEYS.filter((k) => sandboxState.enemyToggles[k]);
}

/**
 * Get the archetype for the current spray type.
 * @returns {import('../config/Enemies.js').EnemyArchetype}
 */
export function getSprayArchetype() {
  return ENEMIES[sandboxState.sprayType] ?? ENEMIES.grunt;
}
/**
 * Toggle delete mode (right-click to remove enemies).
 * @returns {boolean} New delete mode state.
 */
export function toggleDeleteMode() {
  sandboxState.deleteMode = !sandboxState.deleteMode;
  return sandboxState.deleteMode;
}

/**
 * Resets every sandbox knob — enemy toggles, modes and cheats — to defaults.
 * @returns {void}
 */
export function resetSandboxState() {
  for (const key of ENEMY_KEYS) sandboxState.enemyToggles[key] = true;
  sandboxState.sprayMode = false;
  sandboxState.deleteMode = false;
  sandboxState.terrainMode = TerrainMode.FULL;
  sandboxState.sprayType = 'grunt';
  sandboxState.cheats.godMode = false;
  sandboxState.cheats.infiniteAmmo = false;
  sandboxState.cheats.oneShotKills = false;
  sandboxState.cheats.immortal = false;
  sandboxState.cheats.pulseFrozen = false;
}
